import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
//import { useSelector } from 'react-redux';
//import { RootState } from '../store';
import api from '../services/api';

const CATEGORIES = {
  clubhouse: 'Clubhouse Photos',
  teeMarkers: 'Tee Marker Photos',
  flags: 'Flag Photos',
  otherBuildings: 'Other Building Photos',
};

const CourseElementsScreen: React.FC = () => {
  const { projectId } = useParams<{ projectId?: string }>();
  const navigate = useNavigate();
  //const { user } = useSelector((state: RootState) => state.auth);

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setSelectedFile(null);
    setCaption('');
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type (only images for course elements)
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please select a valid image file.');
        return;
      }
      // Validate file size (15MB limit)
      if (file.size > 15 * 1024 * 1024) {
        alert('File size must be less than 15MB.');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedCategory || !selectedFile || !projectId) {
      alert('Please select a category and file first.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('projectId', projectId);
      formData.append('category', selectedCategory);
      if (caption.trim()) {
        formData.append('caption', caption.trim());
      }
      formData.append('media', selectedFile);

      await api.post('/course-elements', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadSuccess(`${CATEGORIES[selectedCategory as keyof typeof CATEGORIES]} uploaded successfully!`);
      setSelectedCategory(null);
      setSelectedFile(null);
      setCaption('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Clear success message after 3 seconds
      setTimeout(() => setUploadSuccess(null), 3000);

    } catch (error: any) {
      console.error('Upload failed:', error);
      alert(`Upload failed: ${error.response?.data?.error || 'Unknown error'}`);
    } finally {
      setUploading(false);
    }
  };

  const resetSelection = () => {
    setSelectedCategory(null);
    setSelectedFile(null);
    setCaption('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-blue-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Course Elements</h1>
          <button
            onClick={() => navigate(`/project/${projectId}`)}
            className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
          >
            Return to Project
          </button>
        </div>

        {uploadSuccess && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
            {uploadSuccess}
          </div>
        )}

        {!selectedCategory ? (
          // Category Selection
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4">Select Course Element Type</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(CATEGORIES).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => handleCategorySelect(key)}
                  className="p-4 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-left transition-colors"
                >
                  <h3 className="font-medium text-blue-800">{label}</h3>
                  <p className="text-sm text-blue-600 mt-1">
                    Upload photos for {label.toLowerCase()}
                  </p>
                </button>
              ))}
            </div>
          </div>
        ) : (
          // Upload Interface
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold">
                Upload {CATEGORIES[selectedCategory as keyof typeof CATEGORIES]}
              </h2>
              <button
                onClick={resetSelection}
                className="text-gray-500 hover:text-gray-700"
              >
                ← Back to categories
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Image File
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                {selectedFile && (
                  <div className="mt-2 text-sm text-gray-600">
                    Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Caption (optional)
                </label>
                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  placeholder="Add a caption for this photo..."
                />
              </div>

              {selectedFile && (
                <div className="bg-gray-50 p-4 rounded-md">
                  <h3 className="font-medium mb-2">Preview:</h3>
                  <img
                    src={URL.createObjectURL(selectedFile)}
                    alt="Preview"
                    className="max-w-full max-h-64 object-contain rounded-md"
                  />
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={resetSelection}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!selectedFile || uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload Photo'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
          <h3 className="font-semibold text-blue-800 mb-2">How to upload course elements:</h3>
          <p className="text-blue-700 text-sm mb-2">
            1. Select the type of course element you want to upload photos for.
          </p>
          <p className="text-blue-700 text-sm mb-2">
            2. Choose image files and add optional captions.
          </p>
          <p className="text-blue-700 text-sm">
            3. Upload to build your course documentation library.
          </p>
        </div>

        {projectId && (
          <div className="mt-4 text-sm text-gray-600">
            Project ID: {projectId}
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseElementsScreen;
